// Copyright (c) Ping. All rights reserved.
//
// Web host for shared AddGroupMembersModal.

import { useCallback } from 'react';

import {
  AddGroupMembersModal as AddGroupMembersModalUi,
  type NewChatModalDirectoryUser,
  pingToast,
  useSession,
} from '@ping/ui';

import { log } from '@core/log/log';
import { ApiNetworkError, type DirectoryUser, lookupUser } from '@core/network';
import { searchUsers } from '@core/network/users';
import { retrieveIdentity } from '@core/persistence/identity';

import {
  addWebGroupMembers,
  EPOCH_DESYNC_MESSAGE,
  GroupCreateError,
  isEpochConflictError,
} from './addWebGroupMembers';

export interface AddGroupMembersModalProps {
  readonly open: boolean;
  readonly onDismiss: () => void;
  readonly conversationId: string;
  readonly existingMemberIds: readonly string[];
}

/** Heuristic: does the query look like an email / phone (an exact 3PID), so it's
 *  worth an exact `lookupUser` in addition to the directory name search? */
function looksLikeContact(q: string): boolean {
  return /@/.test(q) || /^\+?[0-9][0-9 ()-]{5,}$/.test(q);
}

function formatAddError(err: unknown): string {
  if (isEpochConflictError(err) || isEpochConflictError((err as { cause?: unknown })?.cause)) {
    return EPOCH_DESYNC_MESSAGE;
  }
  if (err instanceof GroupCreateError) {
    return err.message;
  }
  if (err instanceof ApiNetworkError) {
    const body = err.serverMessage;
    if (body !== undefined && body.length > 0) return body;
  }
  if (err instanceof Error && err.message.length > 0) {
    return err.message;
  }
  return "Couldn't add those members. Try again.";
}

export function AddGroupMembersModal(props: AddGroupMembersModalProps): JSX.Element {
  const { open, onDismiss, conversationId, existingMemberIds } = props;
  const { client } = useSession();

  const handleSearch = useCallback(
    async (query: string, signal: AbortSignal): Promise<readonly NewChatModalDirectoryUser[]> => {
      const trimmed = query.trim();
      if (trimmed.length === 0) return [];
      // Directory NAME search UNION exact-3PID lookup — mirrors NewGroupModal so
      // the "Add people" box finds users by name (opted-in / discoverable), not
      // only by an exact email/phone. `allSettled` keeps partial results when one
      // source fails; a genuine (non-abort) error is surfaced only if BOTH fail so
      // the modal can show "searching too fast" (429) etc. instead of silently
      // showing an empty list.
      const [dirRes, exactRes] = await Promise.allSettled([
        searchUsers({ query: trimmed, limit: 30, signal, skipReauth: true }).then(
          (r) => r.users,
        ),
        looksLikeContact(trimmed)
          ? lookupUser({ identifier: trimmed, signal }).then((r) =>
              r.user !== null ? [r.user] : [],
            )
          : Promise.resolve([] as DirectoryUser[]),
      ]);
      const dir = dirRes.status === 'fulfilled' ? dirRes.value : [];
      const exact = exactRes.status === 'fulfilled' ? exactRes.value : [];
      if (dirRes.status === 'rejected' && exactRes.status === 'rejected') {
        if (!signal.aborted) throw dirRes.reason;
        return [];
      }
      // Exact 3PID hit wins ordering (most-intentional match first), then name
      // matches; dedup by account id.
      const byId = new Map<string, NewChatModalDirectoryUser>();
      for (const u of [...exact, ...dir]) {
        if (byId.has(u.id)) continue;
        byId.set(u.id, u as NewChatModalDirectoryUser);
      }
      return Array.from(byId.values());
    },
    [],
  );

  const handleAdd = useCallback(
    async (members: readonly NewChatModalDirectoryUser[]): Promise<void> => {
      if (client === null) {
        throw new Error('Still signing you in. Try again in a moment.');
      }

      const identity = await retrieveIdentity();
      const toAdd = members
        .filter((m) => m.id !== identity?.accountId)
        .map(
          (m): { accountId: string; displayName: string } => ({
            accountId: m.id,
            displayName: m.displayName,
          }),
        );

      try {
        const result = await addWebGroupMembers({
          client: client as unknown as Parameters<typeof addWebGroupMembers>[0]['client'],
          conversationId,
          members: toAdd,
        });
        log.info({
          event: 'addGroupMembers.ok',
          conversationIdPrefix: conversationId.slice(0, 8),
          addedCount: result.addedPeerAccountIds.length,
        });
        pingToast.success({
          title:
            result.addedPeerAccountIds.length === 1
              ? 'Member added'
              : `${result.addedPeerAccountIds.length} members added`,
        });
      } catch (err) {
        throw new Error(formatAddError(err), { cause: err });
      }
    },
    [client, conversationId],
  );

  return (
    <AddGroupMembersModalUi
      open={open}
      onDismiss={onDismiss}
      existingMemberIds={existingMemberIds}
      onSearch={handleSearch}
      onAdd={handleAdd}
    />
  );
}
